import { html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';

/**
 * User menu dropdown with username display, help link, and logout.
 *
 * @fires logout - Fired when logout is clicked
 */
@customElement('user-menu')
export class UserMenu extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: block;
                position: fixed;
                top: 1rem;
                right: 1rem;
                z-index: 100;
            }

            .menu-btn {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                padding: 0.5rem 1rem;
                font-size: 0.875rem;
                font-weight: 600;
                background-color: var(--surface-light);
                color: var(--text-secondary);
                border: 2px solid var(--border);
                border-radius: 0.375rem;
                cursor: pointer;
                transition: all 0.2s;
                white-space: nowrap;
            }

            .menu-btn:hover {
                border-color: var(--primary-color);
                color: var(--text-primary);
            }

            .username {
                font-weight: 500;
            }

            .arrow {
                font-size: 0.65rem;
                transition: transform 0.2s;
            }

            .menu-btn.active .arrow {
                transform: rotate(180deg);
            }

            .dropdown {
                position: absolute;
                top: calc(100% + 0.5rem);
                right: 0;
                min-width: 100%;
                background-color: var(--surface-light);
                border: 2px solid var(--border);
                border-radius: 0.375rem;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
                overflow: hidden;
            }

            .menu-item {
                display: block;
                width: 100%;
                padding: 0.75rem 1rem;
                font-size: 0.875rem;
                font-weight: 500;
                color: var(--text-secondary);
                background: none;
                border: none;
                text-align: left;
                cursor: pointer;
                transition: all 0.2s;
                text-decoration: none;
            }

            .menu-item:hover {
                background-color: var(--danger-color);
                color: white;
            }

            a.menu-item {
                text-decoration: none;
            }

            a.menu-item:hover {
                background-color: var(--primary-color);
            }
        `,
    ];

    @property({ type: String })
    username = '';

    @property({ type: String, attribute: 'help-url' })
    helpUrl = '';

    @property({ type: Boolean, reflect: true })
    open = false;

    private _boundCloseOnOutsideClick = this._closeOnOutsideClick.bind(this);

    connectedCallback(): void {
        super.connectedCallback();
        document.addEventListener('click', this._boundCloseOnOutsideClick);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        document.removeEventListener('click', this._boundCloseOnOutsideClick);
    }

    private _closeOnOutsideClick(e: Event): void {
        if (!this.open) return;
        const path = e.composedPath();
        if (!path.includes(this)) {
            this.open = false;
        }
    }

    private _toggle(e: Event): void {
        e.stopPropagation();
        this.open = !this.open;
    }

    private _onLogout(): void {
        this.open = false;
        this.dispatchEvent(
            new CustomEvent('logout', {
                bubbles: true,
                composed: true,
            })
        );
    }

    render() {
        return html`
            <button class="menu-btn ${this.open ? 'active' : ''}" @click=${this._toggle}>
                <span class="username">${this.username}</span>
                <span class="arrow">\u25BC</span>
            </button>
            ${this.open ? html`
                <div class="dropdown">
                    ${this.helpUrl ? html`
                        <a href=${this.helpUrl} target="_blank" class="menu-item">Help</a>
                    ` : nothing}
                    <button class="menu-item" @click=${this._onLogout}>Log Out</button>
                </div>
            ` : nothing}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'user-menu': UserMenu;
    }
}
